import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment } from "react";
import { Field } from "formik";
import { styled } from "../../theme";
import { FlexRow } from "../../box";
import { RadioItem } from "../atoms/radio-item";
import { FieldError } from "../atoms/field-error";
import { FieldLabel } from "../atoms/FieldLabel";
import { LockIcon } from "../atoms/LockIcon";
import { OptionItem, OptionItemChildren } from "../atoms/option-item";
/**
 * `GroupLabel` is a styled component based on `FieldLabel`. It is used to style labels in a group context with additional customization options.
 *
 * Styles applied:
 * - Adds a `margin-bottom` of 0.625em to create spacing between the label and other elements.
 * - Enables word breaking to ensure text wrapping in case of long content.
 *
 * Special State:
 * - When the `disabled` class is applied:
 *   - Reduces the opacity to 0.6, indicating a disabled state.
 *   - Changes the cursor to `not-allowed` for visual feedback.
 *   - Applies a `saturate(25%)` filter to visually differentiate the label.
 */
const GroupLabel = styled(FieldLabel) `
  margin-bottom: 0.625em;
  word-break: break-word;

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    filter: saturate(25%);
  }
`;
/**
 * StyledBlock is a styled div component that applies flexbox layout styling.
 * It is configured to display its children in a columnar layout using flexbox.
 *
 * Styles applied:
 * - display: flex; (Enables flexbox layout on the element)
 * - flex-direction: column; (Arranges children in a vertical column)
 */
const StyledBlock = styled.div `
  display: flex;
  flex-direction: column;
`;
/**
 * A component that displays a group of radio buttons.
 *
 * @param {RadioGroupFieldProps<T>} props The props for the component.
 * @returns {JSX.Element} The rendered radio group field component.
 */
export const RadioGroupField = ({ disabled, label, name, options, renderOption, onChange, OptionChildren, getOptionLabel, getOptionValue, getOptionKey, isChecked, getRawOptionValue, FormField, }) => {
    return (_jsxs(StyledBlock, { children: [label && _jsx(GroupLabel, { disabled: disabled, children: label }), _jsx(FieldError, { name: name }), _jsxs(FlexRow, { children: [_jsx(FormField, { name: name, children: ({ form, field }) => (_jsx(OptionItem, { children: options.map((option, index) => {
                                const checked = isChecked(option, field.value);
                                const element = (_jsx(RadioItem, { ...field, value: getOptionValue(option), label: getOptionLabel(option), helperLabel: option?.helperLabel, disabled: disabled || option.disabled, checked: checked, onChange: (event) => {
                                        field.onBlur(event);
                                        form.setFieldValue(name, getRawOptionValue(option), true);
                                        onChange(event);
                                    }, testId: `radio-${name}-${getOptionValue(option)}`, children: checked && option.children && (_jsx(OptionItemChildren, { children: OptionChildren({
                                            children: option.children,
                                            index,
                                        }) })) }));
                                return (_jsx(Fragment, { children: renderOption(element, option, index) }, getOptionKey(option, name)));
                            }) })) }), disabled && _jsx(LockIcon, {})] })] }));
};
RadioGroupField.defaultProps = {
    renderOption: (element) => element,
    onChange: () => { },
    getOptionLabel: (option) => option.label,
    getOptionValue: (option) => `${option.value}`,
    getOptionKey: (option, name) => `${name}.${option.value}`,
    isChecked: (option, fieldValue) => fieldValue === option.value,
    getRawOptionValue: (option) => option.value,
    FormField: Field,
};
